import { useMemo } from 'react'
import { getResponseColor } from '@/utils/colors'
import type { ScaleType } from '@/types'
import type { ChartSegmentData } from '@/hooks/useChartData'

interface HorizontalStackedBarProps {
  segments: ChartSegmentData[]
  scaleType: ScaleType
  questionText?: string
  sampleSize?: number
  height?: number
  showLabels?: boolean
  direction?: 'ltr' | 'rtl' // For divergent charts: rtl makes bar grow right-to-left
  colorGetter?: (label: string, index: number) => string // Custom color function
  normalize?: boolean // Scale percentages to fill 100% of bar width
  showCounts?: boolean // Show actual counts instead of percentages (for multi-select)
  tooltipLabelSuffix?: string // Suffix to add to label in tooltip (e.g., " Protected")
  maxScale?: number // Max percentage for scaling (e.g., 60 means 60% fills full width)
}

export function HorizontalStackedBar({
  segments,
  scaleType,
  questionText,
  sampleSize,
  height = 32,
  showLabels = true,
  direction = 'ltr',
  colorGetter,
  normalize = false,
  showCounts = false,
  tooltipLabelSuffix = '',
  maxScale,
}: HorizontalStackedBarProps) {
  // Get color for a segment - use custom function if provided
  const getColor = (label: string, index: number) => {
    if (colorGetter) return colorGetter(label, index)
    return getResponseColor(scaleType, label)
  }

  // Calculate cumulative positions for stacked bar
  const barSegments = useMemo(() => {
    // Calculate total for normalization
    const total = segments.reduce((sum, s) => sum + s.percentage, 0)
    // Scale factor: normalize to 100%, or scale to maxScale, or use raw percentages
    let scale = 1
    if (normalize && total > 0) {
      scale = 100 / total
    } else if (maxScale && maxScale > 0) {
      scale = 100 / maxScale
    }

    let cumulative = 0
    return segments.map((segment, index) => {
      const start = cumulative
      const scaledWidth = segment.percentage * scale
      cumulative += scaledWidth
      return {
        ...segment,
        start,
        width: scaledWidth,
        originalPercentage: segment.percentage, // Keep original for display
        index,
      }
    })
  }, [segments, normalize, maxScale])

  if (segments.length === 0) {
    return (
      <div className="flex items-center justify-center h-12 text-slate-400 dark:text-slate-500 text-sm">
        No data available
      </div>
    )
  }

  // Generate accessible description of the data
  const accessibleDescription = segments.length > 0
    ? `Stacked bar chart showing ${questionText || 'response distribution'}. ` +
      segments.map(s => `${s.label}: ${s.percentage.toFixed(1)}%`).join(', ')
    : 'No data available'

  return (
    <div
      className="w-full"
      role="figure"
      aria-label={accessibleDescription}
    >
      {/* Question text with sample size */}
      {questionText && (
        <div className="flex justify-between items-start mb-2">
          <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 flex-1 pr-4">
            {questionText}
          </h4>
        </div>
      )}

      {/* Legend ABOVE the bar - matching Tableau layout */}
      {showLabels && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 mb-2">
          {segments.map((segment, idx) => (
            <div key={segment.label} className="flex items-center gap-1.5 text-xs">
              <div
                className="w-3 h-3 rounded-sm flex-shrink-0"
                style={{ backgroundColor: getColor(segment.label, idx) }}
              />
              <span className="text-slate-600 dark:text-slate-400">
                {segment.label}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Stacked bar with percentages on segments */}
      <div
        className="relative w-full rounded overflow-hidden"
        style={{ height }}
      >
        {barSegments.map((segment) => {
          // Use original percentage for display, width for positioning
          const displayPct = segment.originalPercentage ?? segment.percentage
          // Calculate count if showCounts is true and sampleSize is available
          const count = showCounts && sampleSize ? Math.round(displayPct * sampleSize / 100) : null
          const displayValue = count !== null ? count.toLocaleString() : `${Math.round(displayPct)}%`
          const tooltipLabel = segment.label + tooltipLabelSuffix
          const titleValue = count !== null
            ? `${tooltipLabel}: ${count.toLocaleString()} (${displayPct.toFixed(1)}%)`
            : `${tooltipLabel}: ${displayPct.toFixed(1)}%`

          return (
            <div
              key={segment.label}
              className="absolute top-0 h-full flex items-center justify-center transition-all duration-200 group"
              style={{
                ...(direction === 'rtl'
                  ? { right: `${segment.start}%` }
                  : { left: `${segment.start}%` }),
                width: `${segment.width}%`,
                backgroundColor: getColor(segment.label, segment.index),
              }}
              title={titleValue}
            >
              {/* Tiered labels: ≥10%: full, 5-9%: smaller, 1-4%: just number */}
              {segment.width >= 10 && (
                <span className="text-xs font-bold text-white drop-shadow-sm">
                  {displayValue}
                </span>
              )}
              {segment.width >= 5 && segment.width < 10 && (
                <span className="text-[10px] font-bold text-white drop-shadow-sm">
                  {displayValue}
                </span>
              )}
              {segment.width >= 1 && segment.width < 5 && (
                <span className="text-[9px] font-medium text-white drop-shadow-sm">
                  {count !== null ? count.toLocaleString() : Math.round(displayPct) < 3 ? `${Math.round(displayPct)}` : `${Math.round(displayPct)}%`}
                </span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
